import { chatText } from "../chat/text.ts";
import { GM_CAPABILITIES, type GmCapability, type GmRole } from "./capabilities.ts";
import { FEEDBACK_PAUSE_MAX_MINUTES } from "./feedback.ts";

/**
 * The moderation actions as shared data (ADR-056 decision 0 and its amendment of 2026-10-04): the six actions a GM
 * records and the four on an account's access to feedback (ADR-081's amendment of 2026-10-05), the capability each
 * needs, the bounds of a freeze, a ban and a feedback pause, and the rule a reason and a note are held to. The server checks every request against these; a page reads them only to decide what to show.
 */

/**
 * The actions, in the order the Moderation window lists them: five of ADR-056 decision 0 and the lift, then the four
 * on feedback access: a pause and its end, the removal and its restoring. Each word is lower-case letters, at most
 * 16, as `moderationOffer` carries them, so a page that does not know a word drops it and keeps its socket. The two
 * words cross (ADR-056's amendment of 2026-10-06, decision 18): the stored `alert` is the Flag, a note for the staff
 * behind `player.flag`, and `message` is the Alert, a pop-up to one player behind `player.message`.
 */
export const MODERATION_ACTIONS = ["kick", "freeze", "warn", "message", "alert", "ban", "lift", "feedbackpause", "feedbackresume", "feedbackoff", "feedbackon"] as const;

/**
 * One moderation action.
 */
export type ModerationAction = (typeof MODERATION_ACTIONS)[number];

/**
 * The four actions on an account's access to feedback, which change the account and are recorded in the ledger
 * alone, never as a row of `moderation`.
 */
export const FEEDBACK_ACCESS_ACTIONS = ["feedbackpause", "feedbackresume", "feedbackoff", "feedbackon"] as const satisfies readonly ModerationAction[];

/**
 * One action on feedback access.
 */
export type FeedbackAccessAction = (typeof FEEDBACK_ACCESS_ACTIONS)[number];

/**
 * The two actions that stand until they end or are lifted, in the order a sentence names them. A lift may name one
 * of them, or the row of one, and then ends that one alone (ADR-056's amendment of 2026-10-06, decision 9).
 */
export const LIFTABLE_ACTIONS = ["ban", "freeze"] as const satisfies readonly ModerationAction[];

/**
 * An action a lift ends.
 */
export type LiftableAction = (typeof LIFTABLE_ACTIONS)[number];

/**
 * The capability each action needs. An alert tells only the staff, so recording a flag is enough (`player.flag`, ADR-112 decision 1.4): a Guide may flag without seeing players. A feedback pause is
 * a lesser sanction than a warning's holder may already give; removing feedback is a ban's judgment.
 */
export const MODERATION_CAPABILITY: Readonly<Record<ModerationAction, GmCapability>> = Object.freeze({
  kick: "player.kick",
  freeze: "player.freeze",
  warn: "player.warn",
  message: "player.message",
  alert: "player.flag",
  ban: "player.ban",
  lift: "player.lift",
  feedbackpause: "player.warn",
  feedbackresume: "player.warn",
  feedbackoff: "player.ban",
  feedbackon: "player.ban",
});

/**
 * The capability the list of actions needs.
 */
export const MODERATION_LIST_CAPABILITY: GmCapability = "player.view";

/**
 * The capability a permanent ban needs beside `player.ban`; only a lead holds it.
 */
export const PERMANENT_BAN_CAPABILITY: GmCapability = "player.ban.permanent";

/**
 * The capability the lead's staff screen needs: reading the staff accounts and designating the badge each wears.
 */
export const STAFF_SCREEN_CAPABILITY: GmCapability = "staff.designate";

/**
 * The longest freeze, in minutes: it ends by itself, so a forgotten one costs a player an hour at most.
 */
export const FREEZE_MAX_MINUTES = 60;

/**
 * A freeze's length when the GM names none.
 */
export const FREEZE_DEFAULT_MINUTES = 15;

/**
 * The grace after a freeze ends, by its time or by a lift, in ticks: 5 seconds in which the character may walk and
 * is otherwise still held, so a release beside a creature or an opponent is not a death sentence (ADR-056's
 * amendment of 2026-10-06, decision 41; the length is the author's choice). 0 removes the grace.
 */
export const FREEZE_GRACE_TICKS = 100;

/**
 * The longest ban that has an end, in minutes: 30 days. A ban of 0 minutes is permanent.
 */
export const BAN_MAX_MINUTES = 43_200;

/**
 * The most code points a reason holds, the chat text rule's own bound.
 */
export const REASON_MAX_CHARS = 200;

/**
 * The most code points a private note holds.
 */
export const NOTE_MAX_CHARS = 200;

/**
 * The rows one page of the list holds.
 */
export const MODERATION_PAGE_SIZE = 25;

/**
 * The actions whose notice stays pending until the player acknowledges it (ADR-056's amendment of 2026-10-06,
 * decision 24): the Alert and the warning. Frozen.
 */
export const ACKNOWLEDGED_ACTIONS: readonly ModerationAction[] = Object.freeze<ModerationAction[]>(["message", "warn"]);

/**
 * Whom a notice reads as from, as the wire and the stored `sent_as` carry it (decision 29): `gameMaster` for Player
 * Support and the roles above it, `guide` for a Guide leader. Frozen.
 */
export const NOTICE_SENDERS: readonly ["gameMaster", "guide"] = Object.freeze(["gameMaster", "guide"] as const);

/**
 * One sender word of a notice.
 */
export type NoticeSender = (typeof NOTICE_SENDERS)[number];

/**
 * The kinds a `staffNotice` carries: the Alert, the warning and, from this build on so that no page ever refuses
 * one, the two sentences that withdraw an acknowledged one (decision 35). Frozen.
 */
export const STAFF_NOTICE_KINDS: readonly ["message", "warn", "rescindedWarn", "rescindedMessage"] = Object.freeze(["message", "warn", "rescindedWarn", "rescindedMessage"] as const);

/**
 * One kind of `staffNotice`.
 */
export type StaffNoticeKind = (typeof STAFF_NOTICE_KINDS)[number];

/**
 * The most unacknowledged pop-ups a family holds before an Alert is refused (decision 25); a warning is never
 * refused for it.
 */
export const PENDING_ALERT_CAP = 3;

/**
 * The most pending notices the gateway sends at a join, oldest first (decision 25).
 */
export const PENDING_SENT_AT_JOIN = 8;

/**
 * The most warnings one family is given in an hour from all staff together (decision 22).
 */
export const WARNS_PER_TARGET_PER_HOUR = 3;

const ACTIONS: ReadonlySet<string> = new Set(MODERATION_ACTIONS);

/**
 * Reports whether a word is one of the actions, as read from a request or a command.
 *
 * @param word The candidate.
 * @returns True for an action's exact lower-case name.
 */
export function isModerationAction(word: string): word is ModerationAction {
  return ACTIONS.has(word);
}

const LIFTABLE: ReadonlySet<string> = new Set(LIFTABLE_ACTIONS);

/**
 * Reports whether a word names an action a lift ends, as read from a request's `ends`.
 *
 * @param word The candidate.
 * @returns True for `ban` or `freeze`, exactly and in lower case.
 */
export function isLiftableAction(word: string): word is LiftableAction {
  return LIFTABLE.has(word);
}

const ACCESS_ACTIONS: ReadonlySet<string> = new Set(FEEDBACK_ACCESS_ACTIONS);

/**
 * Reports whether an action is one of the four on feedback access.
 *
 * @param action The action, or any word.
 * @returns True for a pause, its end, the removal or its restoring.
 */
export function isFeedbackAccessAction(action: string): action is FeedbackAccessAction {
  return ACCESS_ACTIONS.has(action);
}

/**
 * The actions a set of capabilities allows, in the actions' order.
 *
 * @param capabilities What an account's live roles grant.
 * @returns The actions; empty for a player.
 */
export function moderationActionsOf(capabilities: readonly GmCapability[]): ModerationAction[] {
  return MODERATION_ACTIONS.filter((action) => capabilities.includes(MODERATION_CAPABILITY[action]));
}

/**
 * The word a moderation offer carries after its actions for an account that may ban permanently (the author's ruling
 * of 2026-10-08, "yeah i would recommend hiding it too"; ADR-056's note of that date). It is a word in the offer's
 * list, not a field of its own, because a page reads the list and drops the words it does not know, where a field it
 * did not know would make it refuse the frame.
 */
export const MODERATION_OFFER_PERMANENT = "permanent";

/**
 * The word a moderation offer carries last for an account that may use the lead's staff screen (`staff.designate`, held
 * by the lead alone). Like `permanent` it is a word in the list a page drops when it does not know it. It decides only
 * whether the tab is drawn; the staff routes check the live roles.
 */
export const MODERATION_OFFER_STAFF = "staff";

/**
 * What `moderationOffer` lists for an account: the actions its capabilities allow, in the actions' order, then
 * `MODERATION_OFFER_PERMANENT` when it may ban permanently, then `MODERATION_OFFER_STAFF` when it holds `staff.designate`.
 *
 * @param capabilities What an account's live roles grant.
 * @returns The words; empty for a player.
 */
export function moderationOfferOf(capabilities: readonly GmCapability[]): string[] {
  const words: string[] = moderationActionsOf(capabilities);
  if (words.includes("ban") && capabilities.includes(PERMANENT_BAN_CAPABILITY)) {
    words.push(MODERATION_OFFER_PERMANENT);
  }
  if (capabilities.includes(STAFF_SCREEN_CAPABILITY)) {
    words.push(MODERATION_OFFER_STAFF);
  }
  return words;
}

/**
 * Whom a notice an account sends reads as from (decision 29): `guide` when at least one of the account's roles grants
 * the action and every one that does is `guide_lead`, `gameMaster` otherwise, so an account that is a Guide leader
 * and also holds a Game Master role sends as a Game Master, and an account no role of which grants the action is
 * answered `gameMaster` too. Worked out once, from the roles read in the action's transaction, and stored.
 *
 * @param roles The sender's live roles, in any order, repeats allowed.
 * @param action The action sent.
 * @returns The sender word.
 */
export function noticeSender(roles: readonly GmRole[], action: ModerationAction): NoticeSender {
  const needed = MODERATION_CAPABILITY[action];
  const granting = roles.filter((role) => GM_CAPABILITIES[role].includes(needed));
  return granting.length > 0 && granting.every((role) => role === "guide_lead") ? "guide" : "gameMaster";
}

/**
 * What an action's minutes come to: the minutes it runs for, null for an action with no length and 0 for a
 * permanent ban, or a refusal.
 */
export type ModerationMinutes = { readonly ok: true; readonly minutes: number | null } | { readonly ok: false };

/**
 * Checks the minutes an action was asked for. A kick, a warning, an Alert, a flag, a lift and, on feedback, a resume, a
 * removal and a restoring take none. A feedback pause must say how long: 1 to `FEEDBACK_PAUSE_MAX_MINUTES`. A freeze runs 1 to
 * `FREEZE_MAX_MINUTES`, `FREEZE_DEFAULT_MINUTES` when none is given. A ban must say how long: 1 to
 * `BAN_MAX_MINUTES`, or 0 for permanent, which needs `PERMANENT_BAN_CAPABILITY`. The action's own capability is
 * the caller's check.
 *
 * @param action The action.
 * @param minutes The minutes asked for, or undefined when the request named none.
 * @param capabilities What the GM's live roles grant.
 * @returns The minutes that take effect, or a refusal.
 */
export function moderationMinutes(action: ModerationAction, minutes: number | undefined, capabilities: readonly GmCapability[]): ModerationMinutes {
  if (minutes !== undefined && (!Number.isSafeInteger(minutes) || minutes < 0)) {
    return { ok: false };
  }
  if (action === "freeze") {
    const asked = minutes ?? FREEZE_DEFAULT_MINUTES;
    return asked >= 1 && asked <= FREEZE_MAX_MINUTES ? { ok: true, minutes: asked } : { ok: false };
  }
  if (action === "ban") {
    if (minutes === undefined || minutes > BAN_MAX_MINUTES) {
      return { ok: false };
    }
    return minutes > 0 || capabilities.includes(PERMANENT_BAN_CAPABILITY) ? { ok: true, minutes } : { ok: false };
  }
  if (action === "feedbackpause") {
    return minutes !== undefined && minutes >= 1 && minutes <= FEEDBACK_PAUSE_MAX_MINUTES ? { ok: true, minutes } : { ok: false };
  }
  return minutes === undefined || minutes === 0 ? { ok: true, minutes: null } : { ok: false };
}

/**
 * Applies the rule a reason and a note are held to: the chat text rule (trimmed, one line, no control or
 * bidirectional control character, at most 200 code points, something visible), then NFC, so the same words are
 * the same text however they were typed. Markup is kept as the text it is; whoever draws it draws text.
 *
 * @param raw The text as sent.
 * @returns The text to store, or null when the rule refuses it.
 * @example
 * moderationText("  spamming Trade  ");
 * // "spamming Trade"
 */
export function moderationText(raw: string): string | null {
  const verdict = chatText(raw);
  return verdict.kind === "ok" ? verdict.text.normalize("NFC") : null;
}
